import { Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  FieldKeys,
  type FieldStatusDto,
  Gate,
  type OrgDto,
  Regulation,
  type SourcingEventDto,
} from "../../../../../../autogen/bff-api";
import { showsHomeDepartment } from "../../../../../../common/departments/departmentVisibility";
import type { FieldError } from "../../../../../../common/editing/save-coordinator/types";
import type { ReadinessLookup, KnownFieldKey } from "../../hooks/useReadinessByField";
import type { PublicEventStep } from "../../wizardSteps";
import { EmptyValue, SummaryField } from "./SummaryField";
import { SummaryFieldGroup } from "./SummaryFieldGroup";
import { SummaryGroupHeader } from "./SummaryGroupHeader";
import { SummarySection } from "./SummarySection";

export const FIELD_KEYS = [
  "TitlePresent",
  "TitleWithinNoticeLimit",
  "HomeDepartmentPresent",
  "RegulationSet",
  "RegulationSupportsPublishing",
  "EstimatedValueSet",
  "EstimatedValueBasisPresent",
  "ContractNatureSet",
  "FoaPartSet",
  "FoaPartUsesPublicFlow",
  "UtilityActivitySet",
] as const satisfies readonly KnownFieldKey[];

interface OverviewSectionProps {
  event: SourcingEventDto;
  refusedTitle?: { savedTitle: string; error: FieldError };
  organization: OrgDto;
  readinessFor: ReadinessLookup;
  onWizardStepNavigate: (step: PublicEventStep) => void;
}

export const OverviewSection = ({
  event,
  refusedTitle,
  organization,
  readinessFor,
  onWizardStepNavigate,
}: OverviewSectionProps) => {
  const { t } = useTranslation();
  const { eventData } = event;
  const publicContext = event.eventContext.type === "Public" ? event.eventContext : null;

  const titleStatus = refusedTitle ? refusedTitleStatus(refusedTitle.error) : readinessFor("TitlePresent");
  const fieldStatuses = FIELD_KEYS.map((key) => (key === "TitlePresent" ? titleStatus : readinessFor(key))).filter(
    (s): s is FieldStatusDto => s !== undefined,
  );
  const title = refusedTitle ? refusedTitle.savedTitle : eventData.title;

  return (
    <SummarySection
      title={t("Procurement overview")}
      editTarget={{ kind: "wizard-step", step: "info" }}
      fieldStatuses={fieldStatuses}
      onWizardStepNavigate={onWizardStepNavigate}
    >
      <SummaryGroupHeader isFirst>{t("General information")}</SummaryGroupHeader>
      <SummaryFieldGroup>
        <SummaryField
          label={t("Title")}
          value={
            <>
              {title || <EmptyValue text={t("Not provided")} />}
              {refusedTitle && (
                <Text fontSize="sm" fontWeight="normal" color="smMuted" mt="1">
                  {t("Your latest change to the title was not saved. This is the title the procurement has.")}
                </Text>
              )}
            </>
          }
          status={titleStatus}
          extraStatuses={[readinessFor("TitleWithinNoticeLimit")]}
        />
        {showsHomeDepartment(organization, event.homeDepartment?.id) && (
          <SummaryField
            label={t("Department")}
            value={
              event.homeDepartment?.name ?? <EmptyValue text={t("Not set")} />
            }
            status={readinessFor("HomeDepartmentPresent")}
          />
        )}
      </SummaryFieldGroup>

      <SummaryGroupHeader>{t("Value and regulation")}</SummaryGroupHeader>
      <SummaryFieldGroup>
        <SummaryField
          label={t("Governing regulation")}
          value={
            publicContext?.regulation ? (
              <>
                {t(publicContext.regulation)}
                {publicContext.regulation === Regulation.Forsyningsforskriften && (
                  <Text fontSize="xs" color="smMuted" fontWeight="normal">
                    {t("Announced as the utilities directive (2014/25/EU)")}
                  </Text>
                )}
              </>
            ) : (
              <EmptyValue text={t("Not set")} />
            )
          }
          span="half"
          status={readinessFor("RegulationSet")}
          extraStatuses={[readinessFor("RegulationSupportsPublishing")]}
        />
        {publicContext?.regulation === Regulation.Forsyningsforskriften && (
          <SummaryField
            label={t("Utility activity")}
            value={
              publicContext.utilityActivity
                ? t(publicContext.utilityActivity)
                : <EmptyValue text={t("Not set")} />
            }
            span="half"
            status={readinessFor("UtilityActivitySet")}
          />
        )}
        <SummaryField
          label={t("Applicable part of the regulation")}
          value={
            publicContext?.foaPart
              ? t(publicContext.foaPart)
              : <EmptyValue text={t("Not classified yet")} />
          }
          span="half"
          status={readinessFor("FoaPartSet")}
        />
        <SummaryField
          label={t("Estimated value")}
          value={
            publicContext?.estimatedValue?.value != null
              ? `${publicContext.estimatedValue.value.toLocaleString("nb-NO")} ${publicContext.estimatedValue.currency}`
              : <EmptyValue text={t("Not set")} />
          }
          span="half"
          status={readinessFor("EstimatedValueSet")}
          extraStatuses={[readinessFor("FoaPartUsesPublicFlow")]}
        />
        <SummaryField
          label={t("Basis for the estimated value (internal)")}
          value={publicContext?.estimatedValueBasis?.trim() ? publicContext.estimatedValueBasis : <EmptyValue text={t("Not set")} />}
          span="full"
          status={readinessFor("EstimatedValueBasisPresent")}
        />
        <SummaryField
          label={t("Contract type")}
          value={
            publicContext?.contractNature
              ? t(publicContext.contractNature)
              : <EmptyValue text={t("Not set")} />
          }
          span="half"
          status={readinessFor("ContractNatureSet")}
        />
      </SummaryFieldGroup>
    </SummarySection>
  );
};

const refusedTitleStatus = (error: FieldError): FieldStatusDto => ({
  fieldKey: FieldKeys.TitlePresent,
  gate: Gate.Publish,
  satisfied: false,
  error: {
    field: "eventData.title",
    code: error.code,
    params: Object.fromEntries(Object.entries(error.params ?? {}).map(([key, value]) => [key, String(value)])),
  },
});
